import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Check,
  ClipboardList,
  Copy,
  ExternalLink,
  Eye,
  Link2,
  Loader2,
  Package,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { CorrectMaterials } from './CorrectMaterials';
import { HelpHint } from './HelpHint';
import { ScrollX } from './ScrollX';

type Material = {
  id: string;
  code: string;
  name: string;
  unit: string;
  quantity: number;
  serial: string | null;
  note: string | null;
};

/**
 * What came back from site, and whether it is enough to close on.
 *
 * Three things used to be scattered or missing. The technician's link was
 * nowhere in the console, so a dispatcher who needed to resend it had to go
 * digging in the message log. What was fitted was recorded nowhere at all.
 * And "has he reported back yet" could only be answered by reading a timeline.
 *
 * The checklist at the top is the same rule the API enforces on close, not a
 * second copy of it — the API sends `report`, and this renders it. A screen
 * that disagreed with the server about whether a job was finished would be
 * worse than no screen.
 */
export function TechnicianReport({
  ticketId,
  status,
  outcome,
  report,
  materials,
  materialsOther,
  completionData,
  assignee,
}: {
  ticketId: string;
  status: string;
  outcome: string | null;
  report?: {
    hasPhotos: boolean;
    hasMaterials: boolean;
    hasCompletionData: boolean;
    hasNotes: boolean;
  };
  materials: Material[];
  materialsOther: string | null;
  completionData: Record<string, unknown> | null;
  assignee: { id: string; name: string } | null;
}) {
  const [copied, setCopied] = useState(false);
  const [sharedCopied, setSharedCopied] = useState(false);

  const status_ = useQuery({
    queryKey: ['ticket-link', ticketId],
    queryFn: () => api.ticketLinkStatus(ticketId),
    // Only meaningful once somebody is actually assigned.
    enabled: Boolean(assignee),
  });

  /**
   * The URL only exists for as long as this component holds it.
   *
   * The database keeps a SHA-256 of the token and nothing else, which is what
   * stops a stolen backup from being a bundle of live job links — so the one
   * that was emailed genuinely cannot be read back. Pressing this issues a
   * fresh link that works identically, and leaves the emailed one alive.
   */
  const issue = useMutation({ mutationFn: () => api.issueTicketLink(ticketId) });
  const resend = useMutation({ mutationFn: () => api.resendTicketLink(ticketId) });

  const copy = async () => {
    if (!issue.data?.url) return;
    await navigator.clipboard.writeText(issue.data.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /** The read-only link, which is not tied to an assignment. */
  const share = useMutation({ mutationFn: () => api.shareTicket(ticketId) });

  const copyShare = async () => {
    if (!share.data?.url) return;
    await navigator.clipboard.writeText(share.data.url);
    setSharedCopied(true);
    setTimeout(() => setSharedCopied(false), 2000);
  };

  // Only meaningful for work that was actually done. A locked gate consumes
  // nothing, and showing an unmet checklist there would be misleading.
  const expectsReport = outcome === null || outcome === 'COMPLETED';
  const closed = status === 'CLOSED' || status === 'CANCELLED';

  return (
    <section className="card">
      <h3 className="mb-1 flex items-center gap-1.5 font-heading text-lg">
        <ClipboardList className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        Technician report
        <HelpHint>
          What came back from site. A completed job cannot be closed until the photos, the items
          used and a root cause are all here — that is what stops work being billed from memory.
        </HelpHint>
      </h3>

      {!assignee && (
        <p className="text-sm text-muted-foreground">
          Nobody is assigned yet, so there is no report and no link.
        </p>
      )}

      {assignee && (
        <>
          {/* ── The link ─────────────────────────────────────────────── */}
          <div className="mt-3 rounded-xl border border-border p-3">
            <div className="flex items-center gap-1.5 text-sm font-medium">
              <Link2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              {assignee.name}&apos;s job link
            </div>

            {status_.isPending && (
              <Loader2 className="mt-2 h-4 w-4 animate-spin text-muted-foreground" />
            )}

            {status_.data && (
              <p className="mt-1 text-xs text-muted-foreground">
                {status_.data.live && status_.data.expiresAt
                  ? `A live link was sent, valid until ${new Date(status_.data.expiresAt).toLocaleString()}.`
                  : 'No live link — the last one has expired or was never sent.'}
              </p>
            )}

            {issue.data?.url ? (
              <>
                <p className="mt-2 break-all rounded-lg bg-muted px-2 py-1.5 font-mono text-xs">
                  {issue.data.url}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Valid until {new Date(issue.data.expiresAt).toLocaleString()}. Opens the job with
                  no password, so treat it like one.
                </p>

                <div className="mt-2 flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary" onClick={() => void copy()}>
                    {copied ? (
                      <Check className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Copy className="h-4 w-4" aria-hidden="true" />
                    )}
                    {copied ? 'Copied' : 'Copy link'}
                  </button>
                  <a href={issue.data.url} target="_blank" rel="noreferrer" className="btn-secondary">
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    Open it
                  </a>
                </div>
              </>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={issue.isPending}
                  onClick={() => issue.mutate()}
                >
                  {issue.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  Show me the link
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={resend.isPending}
                  onClick={() => resend.mutate()}
                >
                  {resend.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  Send it again
                </button>
              </div>
            )}

            {/*
              A different link for a different job. The one above opens the job
              with no password and can act on it, so it goes to one technician
              and is treated like a credential. This one only reads, which is
              what you want when a supervisor asks "what happened on 1042?" —
              and it works whether or not anybody is assigned yet.
            */}
            <div className="mt-3 border-t border-border pt-3">
              {share.data?.url ? (
                <>
                  <p className="break-all rounded-lg bg-muted px-2 py-1.5 font-mono text-xs">
                    {share.data.url}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Read-only, valid for 7 days. Shows the job and what was used, but not the
                    customer&apos;s number, the access notes or the photos.
                  </p>
                  <button
                    type="button"
                    className="btn-secondary mt-2"
                    onClick={() => void copyShare()}
                  >
                    {sharedCopied ? (
                      <Check className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Copy className="h-4 w-4" aria-hidden="true" />
                    )}
                    {sharedCopied ? 'Copied' : 'Copy read-only link'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={share.isPending}
                  onClick={() => share.mutate()}
                >
                  {share.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  <Eye className="h-4 w-4" aria-hidden="true" />
                  Get a read-only link
                </button>
              )}
            </div>

            {resend.isSuccess && (
              <p className="mt-2 text-sm text-secondary-700">Sent to {assignee.name} again.</p>
            )}
            {(resend.isError || issue.isError) && (
              <p className="mt-2 text-sm text-danger-600">
                {((resend.error ?? issue.error) as Error).message}
              </p>
            )}
          </div>

          {/* ── Is it enough to close on ──────────────────────────────── */}
          {expectsReport && report && (
            <ul className="mt-3 space-y-1.5 text-sm">
              {[
                ['Photos from site', report.hasPhotos],
                ['Items used', report.hasMaterials],
                ['Completion form', report.hasCompletionData],
              ].map(([label, done]) => (
                <li key={String(label)} className="flex items-center gap-2">
                  {done ? (
                    <Check className="h-4 w-4 shrink-0 text-secondary-600" aria-hidden="true" />
                  ) : (
                    <X className="h-4 w-4 shrink-0 text-danger-500" aria-hidden="true" />
                  )}
                  <span className={done ? '' : 'text-muted-foreground'}>{label}</span>
                </li>
              ))}
            </ul>
          )}

          {expectsReport && report && !closed && (
            <p className="mt-2 text-xs text-muted-foreground">
              {report.hasPhotos && report.hasMaterials
                ? 'Everything needed is here. This job can be closed.'
                : 'The job cannot be closed until the missing items above arrive.'}
            </p>
          )}

          {/* ── What was used ────────────────────────────────────────── */}
          {/*
            Always rendered now, not only when something was recorded. An empty
            list is exactly when an admin most needs the correction button —
            previously a job with nothing logged offered no way to log it.
          */}
          {(
            <div className="mt-4 border-t border-border pt-3">
              <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
                <Package className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                Used on site
              </h4>

              {materials.length === 0 && !materialsOther && (
                <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>
              )}

              {materials.length > 0 && (
                <ScrollX>
                  <table className="w-full text-sm">
                    <thead className="border-b border-border text-left text-xs text-muted-foreground">
                      <tr>
                        <th className="py-1.5 font-medium">Item</th>
                        <th className="py-1.5 text-right font-medium">Qty</th>
                        <th className="py-1.5 font-medium">Serial</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {materials.map((line) => (
                        <tr key={line.id}>
                          <td className="py-1.5">{line.name}</td>
                          <td className="py-1.5 text-right tabular-nums">
                            {line.quantity}
                            {line.unit === 'metre' ? ' m' : ''}
                          </td>
                          <td className="py-1.5 font-mono text-xs text-muted-foreground">
                            {line.serial ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollX>
              )}

              {materialsOther && (
                <div className="mt-3 rounded-lg bg-muted p-2.5 text-sm">
                  <p className="text-xs font-medium text-muted-foreground">Other items</p>
                  <p className="mt-0.5 whitespace-pre-wrap">{materialsOther}</p>
                </div>
              )}

              {/* Renders nothing without material:record — a dispatcher sees
                  the table above and no way to change it. */}
              <CorrectMaterials
                ticketId={ticketId}
                initial={materials.map((line) => ({
                  code: line.code,
                  quantity: Number(line.quantity),
                  serial: line.serial ?? undefined,
                }))}
                initialOther={materialsOther ?? ''}
              />
            </div>
          )}

          {/* ── The completion form ───────────────────────────────────── */}
          {completionData && Object.keys(completionData).length > 0 && (
            <div className="mt-4 border-t border-border pt-3">
              <h4 className="mb-2 text-sm font-semibold">What they recorded</h4>
              <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                {Object.entries(completionData).map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-xs capitalize text-muted-foreground">
                      {key.replace(/[_-]/g, ' ')}
                    </dt>
                    <dd className="mt-0.5">
                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </>
      )}
    </section>
  );
}
